import { useEffect, useRef, useState, type CSSProperties } from "react";
import { loadAdminStats, loadCheckinDashboard } from "./api";
import type { AdminStats, CheckinDashboard as CheckinDashboardData } from "./types";
import "./checkin-admin.css";

const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const POLL_KEY = "conference.admin.checkinStatsPollMs";
const DEFAULT_POLL_MS = 900_000;
const ALLOWED_POLL_MS = new Set([60_000, 300_000, DEFAULT_POLL_MS]);

export function CheckinDashboard({ refreshKey, onAuthError, onStats }: { refreshKey: number; onAuthError: () => void; onStats?: (value: AdminStats) => void }) {
  const [date, setDate] = useState(today);
  const [affiliation, setAffiliation] = useState<"all" | "church" | "other">("all");
  const [cell, setCell] = useState<"all" | "yes" | "no">("all");
  const [data, setData] = useState<CheckinDashboardData | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [version, setVersion] = useState(0);
  const [pollMs, setPollMs] = useState(() => {
    const stored = Number(localStorage.getItem(POLL_KEY));
    return ALLOWED_POLL_MS.has(stored) ? stored : DEFAULT_POLL_MS;
  });
  const statsRequestActive = useRef(false);

  useEffect(() => {
    let current = true;
    let timer: number | null = null;
    const clearTimer = () => { if (timer !== null) window.clearTimeout(timer); timer = null; };
    const schedule = () => {
      clearTimer();
      if (!current || document.hidden) return;
      timer = window.setTimeout(() => { void refresh(); }, pollMs);
    };
    const refresh = async () => {
      if (!current || document.hidden || statsRequestActive.current) return;
      clearTimer();
      statsRequestActive.current = true;
      try {
        const value = await loadAdminStats();
        if (!current) return;
        setStats(value);
        onStats?.(value);
      } catch (cause) {
        if (current && (cause as { status?: number }).status === 401) onAuthError();
      } finally {
        statsRequestActive.current = false;
        schedule();
      }
    };
    const onVisibilityChange = () => {
      clearTimer();
      if (!document.hidden) void refresh();
    };
    if (!document.hidden) void refresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("conference-checkins");
    if (channel) channel.onmessage = () => { void refresh(); };
    return () => { current = false; clearTimer(); document.removeEventListener("visibilitychange", onVisibilityChange); channel?.close(); };
  }, [onAuthError, onStats, pollMs, refreshKey]);

  useEffect(() => {
    if (version === 0) return;
    let current = true;
    setBusy(true);
    setError("");
    loadCheckinDashboard(date, affiliation, cell).then((value) => { if (current) setData(value); }).catch((cause) => {
      if (!current) return;
      setError(cause instanceof Error ? cause.message : "Falha ao carregar check-ins.");
      if ((cause as { status?: number }).status === 401) onAuthError();
    }).finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, [onAuthError, version]);

  const max = Math.max(1, ...(data?.hourly || []).map((item) => item.count));
  return <section className="checkin-admin-block">
    <article className="dashboard-card checkin-metric"><small>ENTRADAS CONFIRMADAS</small><div><strong>{stats?.totalCheckins ?? "—"}</strong><span>{stats ? "Contador atualizado" : "Carregando…"}</span></div><label>Atualizar a cada <select value={pollMs} onChange={(event) => { const value = Number(event.target.value); localStorage.setItem(POLL_KEY, String(value)); setPollMs(value); }}><option value={60_000}>1 minuto</option><option value={300_000}>5 minutos</option><option value={900_000}>15 minutos</option></select></label></article>
    <article className="dashboard-card checkin-hour-card">
      <div className="checkin-hour-head"><div><small>RITMO DO CHECK-IN</small><h2>Check-ins por hora</h2><p>Consulta detalhada somente sob comando.</p></div><button onClick={() => setVersion((value) => value + 1)} disabled={busy}>{busy ? "Atualizando…" : data ? "Atualizar gráfico" : "Carregar gráfico"}</button></div>
      <div className="checkin-filters"><label>Data<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label><label>Vínculo<select value={affiliation} onChange={(event) => setAffiliation(event.target.value as typeof affiliation)}><option value="all">Todos</option><option value="church">Da igreja</option><option value="other">Outras igrejas</option></select></label><label>Célula<select value={cell} onChange={(event) => setCell(event.target.value as typeof cell)}><option value="all">Todos</option><option value="yes">Participa</option><option value="no">Não participa</option></select></label></div>
      {error && <p className="admin-error">{error}</p>}
      {!data && !busy && <p className="checkin-chart-placeholder">Carregue o gráfico quando precisar analisar o ritmo por hora.</p>}
      <div className="hour-chart" aria-label="Gráfico interativo de check-ins por hora">{(data?.hourly || []).map((item) => {
        const barHeight = Math.max(item.count ? 8 : 2, item.count / max * 82);
        return <button key={item.hour} className={selected === item.hour ? "selected" : ""} style={{ "--height": `${barHeight}%` } as CSSProperties} onClick={() => setSelected(item.hour)} onFocus={() => setSelected(item.hour)} aria-label={`${item.hour} horas: ${item.count} check-ins`}><span className="hour-tooltip"><b>{item.count} check-ins</b><small>{Math.round(item.percentage)}% · {item.cumulative} acumulados</small></span>{item.count > 0 && <span className="hour-value" aria-hidden="true">{item.count}</span>}<i style={{ height: `${barHeight}%` }} /><small>{String(item.hour).padStart(2, "0")}h</small></button>;
      })}</div>
    </article>
  </section>;
}
